import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { setSafFullResponse } from '../../actions/peripheral'
import { syncNextOrderToPlatform, syncOrdersRequested, syncReplayAuthOrdersRequested } from '../../actions/order'
import { setSafIsUploading, setDataIsUploading, setSafStatusRequesting, setSafStatusProgress } from '../ActionCreators'

import { getNumericSafValue, getIsPaxDevice } from '../../selectors/peripheral'
import { getSafIsUploading, getDataIsUploading, getSafStatusRequesting, getSafStatusProgress } from '../Selectors'

import { getSafMode } from '../../selectors/appState'
import { getNetworkAvailable } from '../../selectors/config'

import { ToastManager } from '../../utils/toastManager'
import PeripheralBridge from '../../utils/peripheralBridge'
import createDataDogLog from '../../utils/datadog'

import { getDeviceMode } from '../../VNMode/Selectors'

import { MODES } from '../../VNMode/Reducer'

import { setRoute } from '../../VNRoutes/ActionCreators'
import { setShouldStopSync, setSomethingToSync } from '../ActionCreators'
import { getIsSyncing, getIsAutomatic, getSyncErrorCallback, getSomethingToSync } from '../Selectors'

import { sleep } from '../../utils/timing'
import { VNSyncModalManager } from './VNSyncModalManager'

import useOrderCounters from '../hooks/useOrderCounters'
import { MODAL_NAMES } from './VNSyncModalManager'

const PLATFORM_AUTO_SYNC_DELAY = 10_000 // 10 seconds

export const OFFLINE_SAF_MODE = 2
export const ONLINE_SAF_MODE = 3

export const SAF_MODE_STATES = {
  2: 'Offline',
  3: 'Online',
}

export const ERRORS_WITH_DESCRIPTIONS = {
  ERROR: 'An Unknown error occurred while syncing. Please try again later',
  'UNKNOWN ERROR': 'An Unknown error occurred while syncing. Please try again later',
  'SERVICE BUSY': 'An Unknown error occurred while syncing. Please try again later',
  'CONNECT ERROR': 'There was an error connecting to the card reader. Ensure the card reader is connected.',
  'COMM ERROR': 'There was an error connecting to the card reader. Ensure the card reader is connected.',
  ABORTED: 'Syncing was aborted.',
  'RECEIVE ERROR': 'There was an error receiving data from the card reader. Ensure the card reader is connected.',
}

export const VNSyncManager = () => {
  const dispatch = useDispatch()

  const safIsUploading = useSelector(getSafIsUploading)
  const dataIsUploading = useSelector(getDataIsUploading)
  const safStatusRequesting = useSelector(getSafStatusRequesting)
  const safStatusProgress = useSelector(getSafStatusProgress)
  const somethingToSync = useSelector(getSomethingToSync)

  // Get order counts for syncing.
  const { getSyncableOfflineLocalIds, getOrderIdsToStateSync, getUnsyncedOrderCounts } = useOrderCounters()
  const platformSyncCount = useMemo(() => getSyncableOfflineLocalIds().length, [getSyncableOfflineLocalIds])
  const stateSyncCount = useMemo(() => getOrderIdsToStateSync(true).length, [getOrderIdsToStateSync])
  const stateSyncCountWithoutSubmittedOrders = useMemo(() => getOrderIdsToStateSync(false).length, [getOrderIdsToStateSync])

  const [activeOfflineCardCount] = useMemo(() => getUnsyncedOrderCounts(), [getUnsyncedOrderCounts])

  const safMode = useSelector(getSafMode)
  const isSyncing = useSelector(getIsSyncing)
  const isAutomatic = useSelector(getIsAutomatic)
  const syncErrorCallback = useSelector(getSyncErrorCallback)
  const cfdMode = useSelector(getDeviceMode)

  const networkAvailable = useSelector(getNetworkAvailable)
  const isPaxDevice = useSelector(getIsPaxDevice)

  const [modalName, setModalName] = useState(null)
  const [modalMessageOverride, setModalMessageOverride] = useState(null)
  const [didAttemptResumeSync, setDidAttemptResumeSync] = useState(false)

  const setModalNameWrapper = useCallback(
    (name, messageOverride = null) => {
      createDataDogLog('info', 'Setting modal name', {
        modalName: name,
        safMode,
        isSyncing,
        dataIsUploading,
        safIsUploading,
        safStatusRequesting,
        safStatusProgress,
        platformSyncCount,
        stateSyncCount,
        activeOfflineCardCount,
      })

      setModalName(name)
      setModalMessageOverride(messageOverride)
    },
    [
      safMode,
      isSyncing,
      dataIsUploading,
      safIsUploading,
      safStatusRequesting,
      safStatusProgress,
      platformSyncCount,
      stateSyncCount,
      activeOfflineCardCount,
    ],
  )

  const currentSyncingStep = useMemo(() => {
    if (!isSyncing) return 0

    return dataIsUploading ? 1 : safIsUploading ? 2 : safStatusRequesting ? 3 : 0
  }, [isSyncing, dataIsUploading, safIsUploading, safStatusRequesting])

  const syncingInProgress = useMemo(() => {
    if (!isSyncing) return false

    return dataIsUploading || safIsUploading || safStatusRequesting
  }, [isSyncing, dataIsUploading, safIsUploading, safStatusRequesting])

  const hasCardsToSync = useMemo(() => {
    return stateSyncCount > 0 || activeOfflineCardCount > 0
  }, [stateSyncCount, activeOfflineCardCount])

  // The definitive check to see if we can sync.
  const canSync = useMemo(() => {
    // If the SAF mode is offline, we cannot sync.
    if (safMode === OFFLINE_SAF_MODE) return false

    // If the card reader is not connected, we cannot sync.
    if (!PeripheralBridge.getBridge()) return false

    // If the network is not available, we cannot sync.
    if (!networkAvailable) return false

    if (!somethingToSync) return false

    return true
  }, [safMode, somethingToSync, networkAvailable])

  const stopSyncCycle = useCallback(() => {
    // Reset the state of the sync.
    dispatch(setShouldStopSync())

    // Reset all sync states.
    dispatch(setSafIsUploading(false))
    dispatch(setSafStatusRequesting(false))
    dispatch(setDataIsUploading(false))
  }, [dispatch])

  const syncErrorCallbackWrapper = useCallback(
    (errorKey, errorMessage) => {
      if (syncErrorCallback) syncErrorCallback(errorKey, errorMessage)

      stopSyncCycle()
      setModalNameWrapper(MODAL_NAMES.PROCESSOR_SYNC_FAILED, errorMessage)
      createDataDogLog('error', 'An error occurred while syncing.', {
        errorKey,
        errorMessage,
      })
    },
    [syncErrorCallback, setModalNameWrapper, stopSyncCycle],
  )

  const onCheckNetworkClick = () => {
    dispatch(setRoute('/debug-info/network'))
  }

  const processorSyncIsComplete = useCallback(
    (response) => {
      const { resultText, status, rawResult, fullResponse, message } = response

      // ignore errors from calling POSLINK too many times for CFD
      if (cfdMode === MODES.POS && message?.includes('POSLINK TRANSACTION ERROR: SEND DATA RECV ACK ERROR')) {
        return false
      }

      // The APK may return a resultText of the string "NULL" when the response is empty.
      const translatedResultStatus =
        rawResult === null || rawResult === 'NULL' || rawResult === undefined ? resultText : rawResult

      // Message: {
      //   Code: ERROR | Timeout
      // }
      // rawResult: UNKNOWN ERROR | CONNECT ERROR | COMM ERROR | USER ABORTED | A task is running, please wait | A transaction is running. And it is canceled. Please retry. | ABORTED | RECEIVE ERROR | SERVICE BUSY
      // resultText: ERROR | NOT FOUND
      let responseErrorKey = null

      for (const [errorTitle] of Object.entries(ERRORS_WITH_DESCRIPTIONS)) {
        if (translatedResultStatus?.includes(errorTitle)) {
          responseErrorKey = errorTitle
        }

        if (message?.code?.includes(errorTitle)) {
          responseErrorKey = errorTitle
        }

        //message may be an stringified object so we'll look in message too
        if (typeof message === 'string' && message?.includes(errorTitle)) {
          responseErrorKey = errorTitle
        }
      }

      if (responseErrorKey) {
        syncErrorCallbackWrapper(responseErrorKey, ERRORS_WITH_DESCRIPTIONS?.[responseErrorKey])
        return false
      }

      // Values expected in a standard PAX SAF response.
      const totalCount = getNumericSafValue(fullResponse?.SAFTotalCount)
      const uploadedCount = getNumericSafValue(fullResponse?.SAFUploadedCount)
      const failedCount = getNumericSafValue(fullResponse?.SAFFailedCount)

      const terminalResultStates = ['OK', 'NOT FOUND']

      // The response is a PAX response if it has the following non-negative integral properties.
      const isPaxResponse = totalCount >= 0 && uploadedCount >= 0 && failedCount >= 0
      if (!isPaxResponse) {
        return terminalResultStates.includes(translatedResultStatus) || terminalResultStates.includes(status)
      }

      // When the total failed cards PLUS the total uploaded cards is more or equal to the total cards,
      // then what is left on the reader can only be failed.
      return totalCount <= uploadedCount + failedCount
    },
    [cfdMode, syncErrorCallbackWrapper],
  )

  const maybeUpdateSafResponse = useCallback(
    (fullResponse) => {
      if (!fullResponse) {
        ToastManager.error('SAF upload statuses not received. Sync statuses may be inaccurate.', { autoClose: false })
        createDataDogLog('error', 'SAF upload statuses not received. Sync statuses may be inaccurate.')
        return
      }

      dispatch(setSafFullResponse(fullResponse))
    },
    [dispatch],
  )

  const shouldFinishProcesssorSync = useCallback(
    (response) => {
      if (!response) return false

      // When a terminal state is reached from the processor sync results, begin syncing statuses from
      // the card reader.
      if (!processorSyncIsComplete(response)) return false

      const { fullResponse } = response
      maybeUpdateSafResponse(fullResponse)

      return true
    },
    [processorSyncIsComplete, maybeUpdateSafResponse],
  )

  const startStatusSyncCycle = useCallback(async () => {
    dispatch(setSafStatusRequesting(true))
    dispatch(setSafStatusProgress(null))

    // need to wait a little while for the pax device to ensure that is finished "doing" whatever pax
    // does to unclog itself.
    await sleep(5000)

    dispatch(syncReplayAuthOrdersRequested())
  }, [dispatch])

  // A callback for the SAF upload process. This is called when the SAF upload is complete.
  const startProcessorSyncCycle = useCallback(async () => {
    dispatch(setSafIsUploading(true))

    const bridgeInstance = PeripheralBridge.getBridge()

    if (!bridgeInstance) {
      setModalNameWrapper(MODAL_NAMES.CARD_READER_CONNECT)
      syncErrorCallbackWrapper('no_bridge', 'No bridge instance available for SAF upload.')
      return
    }

    const syncCycleCallbackWrapper = (response) => {
      // sync has been stopped externally
      if (!isSyncing) return

      // TODO (T46565):
      // when CFD, only stop uploading if resultText is OK
      // this suppresses POSLINK TRANSACTION ERROR: SEND DATA RECV ACK ERROR
      // what happens is that when we are calling `replayRequests`, APK seems to be calling BP/POSLINK
      // before we finish receive the last response
      // as a result, we are getting that error message from POSLINK that is triggering an early stop of the sync cycle
      // only listen for paymentProcessor results
      if (cfdMode === MODES.POS && response?.paymentProcessor === undefined) {
        return
      }

      dispatch(setSafIsUploading(false))

      // adding this as a safeguard to clear listening to handleSAResult
      bridgeInstance.registerHandler('handleSAFResult', (responseJson) => {})

      if (!shouldFinishProcesssorSync(response)) return

      startStatusSyncCycle(dispatch, 1)
    }

    if (cfdMode === MODES.POS) {
      bridgeInstance.registerHandler('handleSAFResult', (responseJson) => {
        syncCycleCallbackWrapper(responseJson)
      })
    }

    bridgeInstance?.callHandler('replayRequests', null, syncCycleCallbackWrapper)
  }, [
    dispatch,
    cfdMode,
    syncErrorCallbackWrapper,
    startStatusSyncCycle,
    shouldFinishProcesssorSync,
    setModalNameWrapper,
    isSyncing,
  ])

  const startPlatformSyncCycle = useCallback(async () => {
    if (platformSyncCount === 0) {
      dispatch(setDataIsUploading(false))
      startProcessorSyncCycle()
      return
    }

    // Do the boolean check at the end of the loop to prevent races.
    dispatch(setDataIsUploading(true))
    do {
      dispatch(syncOrdersRequested())
      await sleep(5000)
    } while (dataIsUploading && platformSyncCount > 0)

    // Ensure that data loading gets shut down properly.
    if (dataIsUploading) {
      dispatch(setDataIsUploading(false))
    }

    startProcessorSyncCycle()
  }, [dispatch, dataIsUploading, platformSyncCount, startProcessorSyncCycle])

  const startSyncCycle = useCallback(() => {
    // Don't re-sync if this is an idle sync and we are currently in a sync.
    if (isAutomatic && (dataIsUploading || safIsUploading || safStatusRequesting)) return

    // TODO (mkramerl): Set these correctly at the start and end of each component.
    dispatch(setDataIsUploading(true))
    dispatch(setSafIsUploading(true))
    dispatch(setSafStatusRequesting(true))

    startPlatformSyncCycle()
  }, [dispatch, startPlatformSyncCycle, isAutomatic, dataIsUploading, safIsUploading, safStatusRequesting])

  const onStopSyncClick = useCallback(
    (message = '[Sync Orders] The attendant manually requested to terminate the sync.') => {
      createDataDogLog('info', message, {
        activeOfflineCardCount,
        safStatusProgress,
        platformSyncCount,
      })
    },
    [activeOfflineCardCount, safStatusProgress, platformSyncCount],
  )

  const displayNetworkModal = useCallback(() => {
    setModalNameWrapper(MODAL_NAMES.NETWORK)
    dispatch(setShouldStopSync())
  }, [dispatch, setModalNameWrapper])

  const displayOfflineModeModal = useCallback(() => {
    setModalNameWrapper(MODAL_NAMES.SYNC_OFFLINE_MODE)
    dispatch(setShouldStopSync())
  }, [dispatch, setModalNameWrapper])

  const maybeResumeSync = useCallback(() => {
    setDidAttemptResumeSync(true)

    if (!isSyncing || !canSync) {
      dispatch(setShouldStopSync())
      dispatch(setSafStatusProgress(null))
      setModalName(null)

      return
    }

    switch (currentSyncingStep) {
      case 1:
        // If we are in the first step of the sync, then we can start the platform sync cycle.
        startPlatformSyncCycle()
        break
      case 2:
        // If we are in the second step of the sync, then we can start the processor sync cycle.
        startProcessorSyncCycle()
        break
      case 3:
        // If we are in the third step of the sync, then we can start the status sync cycle.
        startStatusSyncCycle()
        break
      default:
        // If we are not in any step of the sync, then we can start the platform sync cycle.
        startSyncCycle()
    }
  }, [
    dispatch,
    isSyncing,
    canSync,
    currentSyncingStep,
    startPlatformSyncCycle,
    startProcessorSyncCycle,
    startStatusSyncCycle,
    startSyncCycle,
  ])

  // This effect is to handle the case where syncing starts and the network is not available.
  useEffect(() => {
    if (!isSyncing) return

    if (!networkAvailable) displayNetworkModal()
    if (!hasCardsToSync) return
    if (safMode === OFFLINE_SAF_MODE) displayOfflineModeModal()
  }, [isSyncing, safMode, networkAvailable, displayNetworkModal, displayOfflineModeModal, hasCardsToSync])

  useEffect(() => {
    if (didAttemptResumeSync) return

    maybeResumeSync()
  }, [didAttemptResumeSync, maybeResumeSync])

  // This effect is to handle the case where the sync is started automatically.
  useEffect(() => {
    if (isSyncing && isAutomatic && canSync) startSyncCycle()

    if (!isSyncing) stopSyncCycle()
  }, [canSync, startSyncCycle, stopSyncCycle, isSyncing, isAutomatic])

  // Platform Auto-Sync - This effect handles "silent" syncing of orders to the platform
  useEffect(() => {
    // If the network is available and we are not syncing, then we can sync to the platform
    // "silently". For each iteration, sync the first local ID in the list of local IDs.
    if (!networkAvailable || isSyncing || platformSyncCount === 0) return
    if (!isPaxDevice) return

    // Wait 10 seconds before syncing the next order
    const autoSyncTimer = setTimeout(() => {
      dispatch(syncNextOrderToPlatform())
    }, PLATFORM_AUTO_SYNC_DELAY)

    // Cleanup function to clear the timer
    return () => {
      clearTimeout(autoSyncTimer)
    }
  }, [dispatch, networkAvailable, platformSyncCount, isSyncing, isPaxDevice])

  // The singular modal controller effect.
  useEffect(() => {
    if (!isSyncing) {
      if (safStatusProgress === 0) setModalNameWrapper(MODAL_NAMES.SYNC_COMPLETION)
      return
    }

    if (canSync && syncingInProgress) {
      setModalNameWrapper(MODAL_NAMES.SYNCING)
    }

    if (!syncingInProgress) {
      setModalNameWrapper(MODAL_NAMES.SYNC_INITIATED)
    }

    if (safMode === OFFLINE_SAF_MODE && hasCardsToSync) {
      displayOfflineModeModal()
      return
    }

    if (!networkAvailable) {
      displayNetworkModal()
      return
    }

    if (!PeripheralBridge.getBridge()) {
      setModalNameWrapper(MODAL_NAMES.CARD_READER_CONNECT)
      return
    }

    // If there is nothing to sync and the sync completion modal is not already displayed,
    // then we can display the no sync modal.
    if (!somethingToSync && safStatusProgress === null && !syncingInProgress) {
      setModalNameWrapper(MODAL_NAMES.NO_SYNC)
      return
    }
  }, [
    isSyncing,
    syncingInProgress,
    canSync,
    safStatusProgress,
    safMode,
    somethingToSync,
    networkAvailable,
    displayOfflineModeModal,
    displayNetworkModal,
    setModalNameWrapper,
    hasCardsToSync,
  ])

  // There are data states managed throughout the application. This effect is to ensure that the
  // sync manager is aware of the current state of the data that needs to be synced.
  useEffect(() => {
    dispatch(setSomethingToSync(stateSyncCountWithoutSubmittedOrders, activeOfflineCardCount, platformSyncCount))
  }, [dispatch, stateSyncCountWithoutSubmittedOrders, activeOfflineCardCount, platformSyncCount])

  // END Properties for the main sync modal.

  return (
    <VNSyncModalManager
      modalName={modalName}
      syncingInProgress={syncingInProgress}
      dataIsUploading={dataIsUploading}
      safIsUploading={safIsUploading}
      safStatusRequesting={safStatusRequesting}
      safStatusProgress={safStatusProgress}
      platformSyncCount={platformSyncCount}
      currentSyncingStep={currentSyncingStep}
      startSyncCycle={startSyncCycle}
      stopSyncCycle={stopSyncCycle}
      onStopSyncClick={onStopSyncClick}
      onCheckNetworkClick={onCheckNetworkClick}
      setModalName={setModalNameWrapper}
      modalMessageOverride={modalMessageOverride}
    />
  )
}
